
import { useState } from 'react';
import { toast } from 'sonner';
import { recordQuizAttempt } from '@/services/modalCampaigns';
import { ModalCampaign, ModalCampaignQuestion } from '@/types/modalCampaigns';

export const useQuizState = (
  initialAttempt: number = 1
) => {
  const [userAnswers, setUserAnswers] = useState<Record<string, string[]>>({});
  const [quizSubmitted, setQuizSubmitted] = useState<boolean>(false);
  const [quizPassed, setQuizPassed] = useState<boolean>(false);
  const [score, setScore] = useState<number>(0);
  const [attemptNumber, setAttemptNumber] = useState<number>(initialAttempt);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);

  const handleAnswer = (questionId: string, selectedOptions: string[]) => {
    setUserAnswers(prev => ({
      ...prev,
      [questionId]: selectedOptions
    }));
  };

  const submitQuiz = async (
    currentCampaign: ModalCampaign | null,
    currentLogId: string | null,
    userId: string | null,
    questions: ModalCampaignQuestion[]
  ) => {
    if (!currentCampaign || !currentLogId || !userId || questions.length === 0) {
      console.error("Cannot submit quiz: missing required data", { 
        hasCampaign: !!currentCampaign, 
        hasLogId: !!currentLogId, 
        hasUserId: !!userId, 
        questionCount: questions.length 
      });
      toast.error('Er is een fout opgetreden bij het indienen van de quiz');
      return;
    }
    
    try {
      // Calculate score
      let totalScore = 0;
      let totalPossibleScore = 0;
      const allQuestions = [...questions];
      
      allQuestions.forEach(question => {
        const userAnswer = userAnswers[question.id] || [];
        const correctOptions = question.correct_options;
        totalPossibleScore += question.points;
        
        // Check if answers match exactly
        const isCorrect = 
          userAnswer.length === correctOptions.length && 
          userAnswer.every(option => correctOptions.includes(option));
        
        if (isCorrect) {
          totalScore += question.points;
        }
      });
      
      setScore(totalScore);
      
      // Check if passed based on min_score if set, otherwise require 100% score
      const requiredMinScore = currentCampaign.min_score !== null ? currentCampaign.min_score : totalPossibleScore;
      const hasPassed = totalScore >= requiredMinScore;
      setQuizPassed(hasPassed);
      
      // Record the attempt
      await recordQuizAttempt(
        currentLogId,
        currentCampaign.id,
        userId,
        totalScore,
        hasPassed,
        attemptNumber,
        userAnswers
      );
      
      setQuizSubmitted(true);
      
      // Show appropriate message based on pass/fail status
      if (hasPassed) {
        toast.success(`Quiz voltooid met ${totalScore}/${totalPossibleScore} punten!`);
      } else {
        toast.error(`Quiz niet volledig juist beantwoord. Score: ${totalScore}/${totalPossibleScore} punten.`);
        
        // Always reset to the first question when the quiz is failed
        setCurrentQuestionIndex(0);
        
        // Only allow another attempt if repeat_on_failure is true and max_attempts not exceeded
        if (currentCampaign.repeat_on_failure) {
          if (currentCampaign.max_attempts === null || attemptNumber < currentCampaign.max_attempts) {
            // Reset for another attempt after a short delay
            setTimeout(() => {
              setQuizSubmitted(false);
              setUserAnswers({});
              setAttemptNumber(prev => prev + 1);
            }, 3000);
          }
        }
      }
    } catch (error) {
      console.error('Error submitting quiz:', error);
      toast.error('Er is een fout opgetreden bij het indienen van de quiz');
    }
  };

  const resetQuiz = () => {
    setUserAnswers({});
    setQuizSubmitted(false);
    setCurrentQuestionIndex(0); // Reset to first question
    setAttemptNumber(prev => prev + 1);
  };

  // Navigate to previous question
  const goToPreviousQuestion = () => {
    setCurrentQuestionIndex(prev => Math.max(0, prev - 1));
  };

  // Navigate to next question
  const goToNextQuestion = (questionsLength: number) => {
    setCurrentQuestionIndex(prev => Math.min(questionsLength - 1, prev + 1));
  };

  return {
    userAnswers,
    quizSubmitted,
    quizPassed,
    score,
    attemptNumber,
    currentQuestionIndex,
    handleAnswer,
    submitQuiz,
    resetQuiz,
    goToPreviousQuestion,
    goToNextQuestion,
    setAttemptNumber
  };
};
